/**
 * Planowanie, ekipy, kalendarz (P4, docs/02 rozdz. 8). Czyste funkcje — wspólne dla API i interfejsu,
 * testowane osobno. Daty dni jako 'RRRR-MM-DD' (bez strefy), godziny jako 'GG:MM' czasu lokalnego
 * Europe/Warsaw (Z-19). Konflikty liczone na minutach doby lokalnej — zmiana czasu nie psuje porównań.
 */

export const TASK_TYPES = {
  wizja: { label: 'Wizja lokalna', execution: false, requiresConditions: false, hourly: true },
  pomiary: { label: 'Pomiary / dobór', execution: false, requiresConditions: false, hourly: true },
  montaz: { label: 'Realizacja', execution: true, requiresConditions: true, hourly: false },
  uruchomienie: { label: 'Uruchomienie', execution: true, requiresConditions: true, hourly: true },
  serwis: { label: 'Serwis / naprawa', execution: false, requiresConditions: false, hourly: true },
  przeglad: { label: 'Przegląd', execution: false, requiresConditions: false, hourly: true },
  inne: { label: 'Inne', execution: false, requiresConditions: false, hourly: false },
  // poprawka po odbiorze (uwagi klienta w protokole) — docs/10 §3.6; bez warunków startu
  poprawka: { label: 'Poprawka', execution: false, requiresConditions: false, hourly: true },
  // „Mój kalendarz” (docs/22): własny wpis pracownika (np. wizyta u klienta) — bez branży, czynności i protokołu; zamyka się sam po terminie
  wlasne: { label: 'Moje zadanie', execution: false, requiresConditions: false, hourly: true },
} as const;
export type TaskType = keyof typeof TASK_TYPES;

/** Etykieta rodzaju zadania. */
export function taskLabel(t: { taskType: TaskType }): string {
  return TASK_TYPES[t.taskType]?.label ?? 'Zadanie';
}
/** Rodzaje zadań do wyboru przy nowym zadaniu (uruchomienie jest częścią realizacji). */
export const NEW_TASK_TYPES: TaskType[] = ['wizja', 'pomiary', 'montaz', 'poprawka', 'inne'];
/** QA qa10302243 P10: realizacji (montaż, uruchomienie) nie tworzy się przy wstrzymanej sprawie / wstrzymanych zakresach. */
export function executionHoldMessage(until: string | null, what: 'sprawa' | 'zakres' = 'sprawa'): string {
  const term = until ? ` do ${until.slice(8, 10)}.${until.slice(5, 7)}` : '';
  return `${what === 'sprawa' ? 'Sprawa wstrzymana' : 'Zakres wstrzymany'}${term} — realizację zaplanujesz po wznowieniu`;
}

export const TASK_STATUSES = {
  do_zaplanowania: 'Do zaplanowania',
  zaplanowane: 'Zaplanowane',
  w_toku: 'W toku',
  wstrzymane: 'Wstrzymane',
  zakonczone: 'Zakończone',
  anulowane: 'Anulowane',
} as const;
export type TaskStatus = keyof typeof TASK_STATUSES;

export const ASSIGNMENT_STATES = { planowany: 'Planowany', faktyczny: 'Faktyczny', odwolany: 'Odwołany' } as const;
export type AssignmentState = keyof typeof ASSIGNMENT_STATES;

export const MEMBERSHIP_KINDS = { stale: 'Stały skład', czasowe: 'Czasowe przejście' } as const;
export type MembershipKind = keyof typeof MEMBERSHIP_KINDS;

export const ENTRY_KINDS = { nieobecnosc: 'Nieobecność', wydarzenie: 'Wydarzenie firmowe', wpis: 'Wpis organizacyjny' } as const;
export type EntryKind = keyof typeof ENTRY_KINDS;

export const ABSENCE_TYPES = { urlop: 'Urlop', l4: 'Zwolnienie lekarskie (L4)', szkolenie: 'Szkolenie', inne: 'Inna nieobecność' } as const;
export type AbsenceType = keyof typeof ABSENCE_TYPES;

export const ABSENCE_STATUSES = { wniosek: 'Wniosek', zatwierdzona: 'Zatwierdzona', odrzucona: 'Odrzucona' } as const;
export type AbsenceStatus = keyof typeof ABSENCE_STATUSES;

/** Rodzaj urlopu we wniosku — informacja dla kadr (system nie liczy wymiaru urlopu, docs/17). */
export const LEAVE_KINDS = {
  wypoczynkowy: 'Wypoczynkowy',
  na_zadanie: 'Na żądanie',
  okolicznosciowy: 'Okolicznościowy',
  bezplatny: 'Bezpłatny',
  opieka: 'Opieka nad dzieckiem (art. 188)',
  inny: 'Inny',
} as const;
export type LeaveKind = keyof typeof LEAVE_KINDS;
export const LEAVE_KIND_KEYS = Object.keys(LEAVE_KINDS) as [LeaveKind, ...LeaveKind[]];

/** Decyzja na poziomie drabinki akceptacji; „pominięta” — osoba była nieobecna albo zdecydował ktoś z wyższego poziomu. */
export const APPROVAL_DECISIONS = { zatwierdzona: 'Zaakceptowano', odrzucona: 'Odrzucono', pominieta: 'Pominięto' } as const;
export type ApprovalDecision = keyof typeof APPROVAL_DECISIONS;

/** Tytuł wpisu nieobecności: rodzaj (dla urlopu innego niż wypoczynkowy — z rodzajem urlopu). */
export function absenceTitle(type: AbsenceType, leaveKind: LeaveKind | null): string {
  if (type === 'urlop' && leaveKind && leaveKind !== 'wypoczynkowy' && leaveKind !== 'inny') return `Urlop ${LEAVE_KINDS[leaveKind].toLowerCase()}`;
  return { urlop: 'Urlop', l4: 'Zwolnienie lekarskie', szkolenie: 'Szkolenie', inne: 'Nieobecność' }[type];
}

// ── daty i godziny ────────────────────────────────────────────────────────

const toUtc = (d: string) => Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));
const fromUtc = (t: number) => new Date(t).toISOString().slice(0, 10);

/** Dzień tygodnia: 1 = poniedziałek … 7 = niedziela. */
export const isoWeekday = (d: string) => ((new Date(toUtc(d)).getUTCDay() + 6) % 7) + 1;
export const isWeekend = (d: string) => isoWeekday(d) >= 6;
/** Data po polsku: „25.10.2026” (z „2026-10-25” albo z ISO z godziną — dzień w strefie Warszawy). */
export function datePl(d: string | null | undefined): string {
  if (!d) return '—';
  const day = d.length === 10 ? d : new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Warsaw' }).format(new Date(d));
  return `${day.slice(8, 10)}.${day.slice(5, 7)}.${day.slice(0, 4)}`;
}

export const shiftDate = (d: string, days: number) => fromUtc(toUtc(d) + days * 86_400_000);
export const dayDiff = (from: string, to: string) => Math.round((toUtc(to) - toUtc(from)) / 86_400_000);

/** Dni zadania [od, do] (włącznie), opcjonalnie bez sobót i niedziel. */
export function taskDays(from: string, to: string, skipWeekends: boolean): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = shiftDate(d, 1)) {
    if (!(skipWeekends && isWeekend(d))) out.push(d);
    if (out.length > 366) break;
  }
  return out;
}

export const timeToMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
export const minToTime = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** Przedział w minutach doby lokalnej: cały dzień = [0, 1440), godziny = [od, do). */
export function dayInterval(timeFrom: string | null, timeTo: string | null): [number, number] {
  if (!timeFrom || !timeTo) return [0, 1440];
  return [timeToMin(timeFrom), timeToMin(timeTo)];
}

export const overlaps = (a: [number, number], b: [number, number]) => a[0] < b[1] && b[0] < a[1];

// ── skład ekipy w czasie ──────────────────────────────────────────────────

export interface MembershipLike {
  teamId: string;
  userId: string;
  kind: MembershipKind;
  validFrom: string;
  validTo: string | null;
}

const activeOn = (m: MembershipLike, day: string) => m.validFrom <= day && (m.validTo === null || m.validTo >= day);

/**
 * Skład ekipy w danym dniu: stali członkowie (bez osób czasowo przeniesionych do innej ekipy tego dnia)
 * + osoby czasowo przeniesione do tej ekipy.
 */
export function teamMembersOn(memberships: MembershipLike[], teamId: string, day: string): string[] {
  const active = memberships.filter((m) => activeOn(m, day));
  const movedAway = new Set(active.filter((m) => m.kind === 'czasowe' && m.teamId !== teamId).map((m) => m.userId));
  const members = new Set<string>();
  for (const m of active) {
    if (m.teamId !== teamId) continue;
    if (m.kind === 'stale' && movedAway.has(m.userId)) continue;
    members.add(m.userId);
  }
  return [...members].sort();
}

/** Osoby w zadaniu danego dnia: skład ekipy + dodani − wyłączeni (ze wskazaniem źródła). */
export function taskPeopleOn(
  memberships: MembershipLike[],
  task: { teamId: string | null; added: string[]; excluded: string[] },
  day: string,
): { userId: string; source: 'ekipa' | 'recznie' }[] {
  const out = new Map<string, 'ekipa' | 'recznie'>();
  if (task.teamId) for (const u of teamMembersOn(memberships, task.teamId, day)) if (!task.excluded.includes(u)) out.set(u, 'ekipa');
  for (const u of task.added) if (!out.has(u)) out.set(u, 'recznie');
  return [...out].map(([userId, source]) => ({ userId, source }));
}

// ── konflikty ─────────────────────────────────────────────────────────────

export type BusyKind = 'zadanie' | 'nieobecnosc' | 'wniosek' | 'wydarzenie' | 'wpis';

/** Zajętość osoby w danym dniu (zadanie, nieobecność, wydarzenie, wpis). */
export interface Busy {
  userId: string;
  day: string;
  timeFrom: string | null;
  timeTo: string | null;
  kind: BusyKind;
  /** identyfikator źródła (zadanie / wpis) — ten sam zapis nie koliduje sam ze sobą */
  sourceId: string;
  label: string;
}

export type ConflictSeverity = 'wysoka' | 'srednia' | 'niska';

export interface Conflict {
  userId: string;
  day: string;
  severity: ConflictSeverity;
  with: { kind: BusyKind; sourceId: string; label: string; timeFrom: string | null; timeTo: string | null };
}

/**
 * Konflikty planowanych zadań z istniejącą zajętością (docs/02 rozdz. 8):
 * zadanie × zadanie (nakładanie przedziałów), × nieobecność zatwierdzona (wysoka), × wniosek (niska),
 * × wydarzenie z udziałem osoby (średnia); wpis organizacyjny — bez konfliktu.
 */
export function findConflicts(planned: Busy[], existing: Busy[]): Conflict[] {
  const byKey = new Map<string, Busy[]>();
  for (const e of existing) {
    if (e.kind === 'wpis') continue;
    const k = `${e.userId}|${e.day}`;
    (byKey.get(k) ?? byKey.set(k, []).get(k)!).push(e);
  }
  const out: Conflict[] = [];
  for (const p of planned) {
    const pi = dayInterval(p.timeFrom, p.timeTo);
    for (const e of byKey.get(`${p.userId}|${p.day}`) ?? []) {
      if (e.sourceId === p.sourceId) continue;
      if (!overlaps(pi, dayInterval(e.timeFrom, e.timeTo))) continue;
      const severity: ConflictSeverity = e.kind === 'nieobecnosc' ? 'wysoka' : e.kind === 'wniosek' ? 'niska' : e.kind === 'wydarzenie' ? 'srednia' : 'wysoka';
      out.push({ userId: p.userId, day: p.day, severity, with: { kind: e.kind, sourceId: e.sourceId, label: e.label, timeFrom: e.timeFrom, timeTo: e.timeTo } });
    }
  }
  return out;
}

export const conflictText = (c: Conflict, personName: string) =>
  `${personName}: ${c.with.label} (${c.day}${c.with.timeFrom ? `, ${c.with.timeFrom}–${c.with.timeTo}` : ', cały dzień'})`;

// ── wpisy planu (odcinki pracy) ───────────────────────────────────────────

export interface SegmentLike {
  dateFrom: string;
  dateTo: string;
  skipWeekends: boolean;
  teamId?: string | null;
}

/**
 * Dni wszystkich wpisów (dzień × wpis). Kilka ekip może pracować przy zadaniu tego samego dnia — błąd tylko,
 * gdy ta sama ekipa jest dwa razy w tym samym dniu albo wpis nie ma żadnego dnia.
 */
export function segmentDays<T extends SegmentLike>(segments: T[]): { days: { day: string; segment: number }[]; error: string | null } {
  const seen = new Map<string, number>();
  const days: { day: string; segment: number }[] = [];
  for (const [i, s] of segments.entries()) {
    const ds = taskDays(s.dateFrom, s.dateTo, s.skipWeekends);
    if (!ds.length) return { days: [], error: s.skipWeekends && s.dateFrom <= s.dateTo ? `Termin ${i + 1}: wybrane dni to sobota/niedziela — odznacz »Bez sobót i niedziel«` : `Termin ${i + 1}: brak dni w wybranym zakresie` };
    for (const d of ds) {
      if (s.teamId) {
        const k = `${d}|${s.teamId}`;
        if (seen.has(k)) return { days: [], error: `Ta sama ekipa jest dwa razy w dniu ${d} (terminy ${seen.get(k)! + 1} i ${i + 1}) — połącz je` };
        seen.set(k, i);
      }
      days.push({ day: d, segment: i });
    }
  }
  days.sort((a, b) => a.day.localeCompare(b.day) || a.segment - b.segment);
  return { days, error: null };
}

/** QA6 P22: margines przy porównaniu startu z umówionym oknem godzinowym (minuty przed początkiem i po końcu okna). */
export const START_WINDOW_MARGIN_MIN = 60;

/** Godzina „HH:MM” chwili w strefie Europe/Warsaw. */
export function timeInWarsaw(at: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Warsaw', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(at);
}

/**
 * QA6 P22: start wyraźnie poza umówionym oknem (więcej niż `margin` minut przed początkiem albo po końcu okna).
 * Termin całodniowy (bez godzin) — nigdy poza oknem.
 */
export function outsideStartWindow(time: string, timeFrom: string | null | undefined, timeTo: string | null | undefined, margin = START_WINDOW_MARGIN_MIN): boolean {
  if (!timeFrom) return false;
  const m = (x: string) => Number(x.slice(0, 2)) * 60 + Number(x.slice(3, 5));
  const t = m(time);
  return t < m(timeFrom) - margin || (!!timeTo && t > m(timeTo) + margin);
}
